import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Transition } from '@headlessui/react';
import {
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Sun,
  Moon,
  User,
  Wallet,
  Receipt,
  CreditCard,
  Zap,
  LogOut,
  Undo2,
  Smartphone,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEffectiveRole } from '../../hooks/useEffectiveRole';
// @ts-ignore
import useAuthorizedModules from '../../hooks/useAuthorizedModules';
import logo from '../../assets/Logo CLU I White.png';
// @ts-ignore
import AuthService from '../../services/auth.service';
import OrganizationSelector from '../shared/OrganizationSelector';
import NotificationBell from '../shared/NotificationBell';
import LanguageSelector from '../shared/LanguageSelector';
import { JobCenterProvider } from '../../features/jobsV5/JobCenterContext';
import { JobCenterBell } from '../../features/jobsV5/JobCenterBell';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import UserService from '../../services/user.service';
import BypassOnboardingModal from '../admin/BypassOnboardingModal';
// @ts-ignore
import PlatformNoticeBar from '../common/PlatformNoticeBar';
// @ts-ignore
import ConnectionStatusBanner from '../common/ConnectionStatusBanner';
import ImpersonationBanner from '../common/ImpersonationBanner';
import { CommandPalette, type PaletteItem } from './CommandPalette';
import { resolveNavIcon } from './iconMap';
import { useTheme } from '../../hooks/useTheme';
import { SETTINGS_CATALOG } from './settingsCatalog';
import { getRoutesByCategory } from '../../config/routes.registry';
import BugReportButton from '../common/BugReportButton';

interface MenuItem {
  path: string;
  name: string;
  icon: string;
  requiredRole?: string;
  moduleCode?: string;
  isDemo?: boolean;
  nameKey?: string;
  section?: string;
}

interface Module {
  id: number;
  name: string;
  code: string;
  description?: string;
  is_basic?: boolean;
  isExtraModule?: boolean;
  originalCode?: string;
  show_in_menu?: boolean;
  route_path?: string;
  display_name?: string;
  icon?: string;
  menu_order?: number;
}

const SIDEBAR_WIDTH = 256;
const SIDEBAR_COLLAPSED_WIDTH = 76;
const COLLAPSE_KEY = 'clu_shell_v2_collapsed';

// Raíces de ruta que se dibujan a pantalla completa: consolas que traen su
// propio scroll interno y se pegan al layout, sin padding. Todo lo demás es
// un documento y va con padding y scroll de página.
//
// `/clu-ng` está listado aparte de `/clu` a propósito, porque la comparación
// es por SEGMENTO y `/clu` no lo cubre. Eso es deliberado: el check anterior
// era `pathname.startsWith('/clu')`, que dejaba afuera a `/operator` —una
// consola de tres columnas con su propio `h-full` y `overflow-hidden`, que
// terminaba tratada como documento— y de paso habría agarrado por accidente
// cualquier ruta futura tipo `/clusters`.
export const FULL_SCREEN_ROOTS = ['/clu', '/clu-ng', '/operator', '/galaxia'];

/** Si esa ruta se dibuja a pantalla completa. Exportada para poder testearla. */
export function isFullScreenRoute(pathname: string): boolean {
  return FULL_SCREEN_ROOTS.some(
    (root) => pathname === root || pathname.startsWith(`${root}/`),
  );
}

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(pointer: coarse)').matches ||
    /iPhone|iPad|iPod|Android|Mobile|Tablet/i.test(navigator.userAgent));

const LayoutV2: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme, isDark } = useTheme();

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.matchMedia('(max-width: 1024px)').matches) return true;
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [showMobileReturn, setShowMobileReturn] = useState(false);

  const { currentUser, userInfo, logout, role, loading, activeOrganization, isImpersonating, impersonatedEmail } = useAuth();
  // @ts-ignore
  const { authorizedModules, isModuleAuthorized } = useAuthorizedModules();
  const typedModules = authorizedModules as Module[];
  const { effectiveRole: hookEffectiveRole, isSuperUser } = useEffectiveRole();

  const [profileAvatarUrl, setProfileAvatarUrl] = useState<string | null>(null);
  const [profileDisplayName, setProfileDisplayName] = useState<string | null>(null);
  const [ccSummary, setCcSummary] = useState<{ pending: number } | null>(null);
  const ccIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setShowMobileReturn(isTouchDevice());
  }, []);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Global ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const userEmail = useMemo(() => {
    if (isImpersonating && impersonatedEmail) return impersonatedEmail;
    return userInfo?.user?.email || currentUser?.email || null;
  }, [isImpersonating, impersonatedEmail, userInfo?.user?.email, currentUser?.email]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!userEmail) {
        setProfileAvatarUrl(null);
        setProfileDisplayName(null);
        return;
      }
      try {
        const url = await UserService.getAvatarObjectUrl(userEmail);
        if (active) setProfileAvatarUrl(url);
      } catch {
        if (active) setProfileAvatarUrl(null);
      }
      try {
        const backendUser = await UserService.getUserByEmail(userEmail);
        if (active) setProfileDisplayName(backendUser?.name || null);
      } catch {
        if (active) setProfileDisplayName(null);
      }
    })();
    return () => { active = false; };
  }, [userEmail]);

  // Control Center pending-commands badge (same registry as Shell V1)
  useEffect(() => {
    let active = true;
    if (ccIntervalRef.current) {
      clearInterval(ccIntervalRef.current);
      ccIntervalRef.current = null;
    }
    const refresh = async () => {
      try {
        const { ControlCenterRegistry } = await import('../../stores/control-center.registry');
        const summary = await ControlCenterRegistry.getSummary();
        if (active && summary) setCcSummary({ pending: summary?.commands?.pending || 0 });
      } catch {
        // best-effort badge
      }
    };
    refresh();
    ccIntervalRef.current = setInterval(refresh, 30000);
    return () => {
      active = false;
      if (ccIntervalRef.current) {
        clearInterval(ccIntervalRef.current);
        ccIntervalRef.current = null;
      }
    };
  }, []);

  const getUserRole = (): string => {
    if (loading || !hookEffectiveRole) return 'loading';
    return hookEffectiveRole;
  };

  const getRoleDisplayName = (roleCode: string): string => {
    if (roleCode === 'loading') return '...';
    if (roleCode === 'super_user') return t('layout_role_super_user');
    if (roleCode === 'admin') return t('layout_role_admin');
    if (roleCode === 'user') return t('layout_role_user');
    return roleCode;
  };

  const hasRoleAccess = (requiredRole?: string): boolean => {
    if (!requiredRole) return true;
    const effectiveRole = getUserRole();
    if (effectiveRole === 'super_user') return true;
    if (effectiveRole === 'admin' && ['admin', 'user'].includes(requiredRole)) return true;
    return requiredRole === 'user' && effectiveRole === 'user';
  };

  const getUserOrganization = () => {
    if (activeOrganization) {
      return activeOrganization.organization_name || t('layout_org_no_name');
    }
    if (loading || !userInfo?.user?.members || userInfo.user.members.length === 0) {
      return '...';
    }
    return userInfo.user.members[0]?.organization?.name || t('layout_org_none');
  };

  const staticMenuItems: MenuItem[] = useMemo(() => [
    { path: '/control-center', name: 'Control Center', nameKey: 'layout_control_center', icon: '🕹️', requiredRole: 'super_user', section: 'Platform' },
    { path: '/admin/automation', name: 'Automation Center', icon: '⚙️', requiredRole: 'super_user', section: 'Platform' },
    { path: '/clerks-ecosystem', name: 'Clerks Ecosystem', nameKey: 'layout_clerks_ecosystem', icon: '🌐', requiredRole: 'super_user', moduleCode: 'clerks_ecosystem_', section: 'Platform' },
    { path: '/clu-ng', name: 'CLU', nameKey: 'layout_clu', icon: '🤖', requiredRole: 'user', section: 'Platform' },
    { path: '/drive', name: 'Drive', icon: '💾', requiredRole: 'user', section: 'Platform' },
    { path: '/galaxia', name: 'Constellation', nameKey: 'layout_constellation', icon: '🌌', requiredRole: 'super_user', section: 'Platform' },
    { path: '/settings', name: 'Settings', nameKey: 'layout_settings', icon: '⚙️', requiredRole: 'super_user', section: 'Platform' },
    { path: '/menu', name: 'Menu', nameKey: 'layout_menu', icon: '📋', requiredRole: 'user', section: 'Platform' },
  ], []);

  const dynamicMenuItems: MenuItem[] = useMemo(() => {
    return typedModules
      .filter((module) => module.show_in_menu === true && !!module.route_path)
      .map((module) => ({
        path: module.route_path as string,
        name: module.display_name || module.name,
        icon: module.icon || '📦',
        moduleCode: module.code || module.name?.toLowerCase().replace(' ', '_'),
        menuOrder: module.menu_order || 0,
        section: 'Workspace',
      }))
      .sort((a: any, b: any) => (a.menuOrder || 0) - (b.menuOrder || 0));
  }, [typedModules]);

  const allMenuItems = useMemo(
    () => [...dynamicMenuItems, ...staticMenuItems],
    [dynamicMenuItems, staticMenuItems]
  );

  const filteredMenuItems: MenuItem[] = useMemo(() => {
    const effectiveRole = getUserRole();
    if (loading || !hookEffectiveRole || effectiveRole === 'loading') return [];

    if (effectiveRole === 'user' || effectiveRole === 'admin') {
      return [
        { path: '/dashboard', name: 'Dashboard', nameKey: 'layout_dashboard', icon: '📊', requiredRole: 'user', isDemo: false, section: 'Navigate' },
        { path: '/clu-ng', name: 'CLU', nameKey: 'layout_clu', icon: '🤖', requiredRole: 'user', isDemo: false, section: 'Navigate' },
        { path: '/drive', name: 'Drive', icon: '💾', requiredRole: 'user', isDemo: false, section: 'Navigate' },
        { path: '/menu', name: 'Menu', nameKey: 'layout_menu', icon: '📋', requiredRole: 'user', isDemo: false, section: 'Navigate' },
      ];
    }

    return allMenuItems
      .filter((item) => hasRoleAccess(item.requiredRole))
      .map((item) => {
        let isDemo = false;
        if (item.moduleCode) {
          const moduleInfo = typedModules.find(
            (m) => m.code === item.moduleCode || (m.originalCode && m.originalCode === item.moduleCode)
          );
          if (moduleInfo && moduleInfo.isExtraModule) isDemo = true;
        }
        return { ...item, isDemo };
      });
  }, [allMenuItems, role, authorizedModules, typedModules, hookEffectiveRole, loading, isSuperUser]);

  const navSections = useMemo(() => {
    const sections = new Map<string, MenuItem[]>();
    for (const item of filteredMenuItems) {
      const key = item.section || 'Navigate';
      const list = sections.get(key) || [];
      list.push(item);
      sections.set(key, list);
    }
    return Array.from(sections.entries());
  }, [filteredMenuItems]);

  const paletteItems: PaletteItem[] = useMemo(() => {
    const effectiveRole = getUserRole();
    let navItems: PaletteItem[] = [];

    if (effectiveRole === 'super_user') {
      navItems = filteredMenuItems.map((item) => ({
        path: item.path,
        name: item.nameKey ? t(item.nameKey) : item.name,
        section: item.section || 'Navigate',
        icon: item.icon,
      }));
      const settingsItems: PaletteItem[] = SETTINGS_CATALOG
        .filter((item) => hasRoleAccess(item.requiredRole))
        .map((item) => ({
          path: item.path,
          name: item.name,
          section: 'Settings',
          iconComponent: item.icon,
          keywords: item.keywords,
        }));
      // ROUTES_REGISTRY entries (e.g. /operator) aren't necessarily in the
      // sidebar or Settings catalog — this was the only branch that never
      // read the registry, so a super_user-only route registered there
      // (roles: ["super_user"]) was invisible to Cmd/Ctrl+K search even
      // though the route itself was correctly gated.
      const seenSoFar = new Set([...navItems, ...settingsItems].map((item) => item.path));
      const registryItems: PaletteItem[] = Object.values(
        getRoutesByCategory(effectiveRole, isModuleAuthorized)
      )
        .flat()
        .filter((route) => !seenSoFar.has(route.path))
        .map((route) => ({
          path: route.path,
          name: route.label,
          section: route.category,
          icon: route.icon,
          keywords: route.keywords,
        }));
      navItems = [...navItems, ...settingsItems, ...registryItems];
    } else {
      const registryRoutes = Object.values(
        getRoutesByCategory(effectiveRole, isModuleAuthorized)
      ).flat();
      const registryItems: PaletteItem[] = registryRoutes.map((route) => ({
        path: route.path,
        name: route.label,
        section: route.category,
        icon: route.icon,
        keywords: route.keywords,
      }));
      const moduleItems: PaletteItem[] = dynamicMenuItems.map((item) => ({
        path: item.path,
        name: item.name,
        section: 'Modules',
        icon: item.icon,
      }));
      const seenPaths = new Set(registryItems.map((item) => item.path));
      navItems = [...registryItems, ...moduleItems.filter((item) => !seenPaths.has(item.path))];
    }

    const quickLinks: PaletteItem[] = [
      { path: '/profile', name: t('header_my_profile'), section: 'Account', iconComponent: User },
      { path: '/my-wallet', name: t('header_my_wallet'), section: 'Account', iconComponent: Wallet },
    ];
    if (hookEffectiveRole === 'admin' || isSuperUser) {
      quickLinks.push({ path: '/billing', name: t('header_my_billing'), section: 'Account', iconComponent: CreditCard });
    }
    return [...navItems, ...quickLinks];
  }, [filteredMenuItems, dynamicMenuItems, hookEffectiveRole, isSuperUser, isModuleAuthorized, t]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      try {
        await AuthService.logout();
        window.location.href = '/login';
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('originalUser');
        window.location.href = '/login';
      }
    }
  };

  const handleBackToClassic = () => {
    localStorage.setItem('clu_shell_v2', '0');
    window.location.reload();
  };

  const pageTitle = useMemo(() => {
    if (location.pathname === '/settings/llm-costs') return t('app_grid_llm_costs');
    if (location.pathname === '/navigator-admin' || location.pathname === '/settings/navigator-admin') return t('app_navigator_admin');
    if (location.pathname === '/navigator-performance' || location.pathname === '/settings/navigator-performance') return t('app_navigator_performance');
    const headerItem = allMenuItems.find((item) => item.path === location.pathname);
    return headerItem ? (headerItem.nameKey ? t(headerItem.nameKey) : headerItem.name) : t('app_grid');
  }, [location.pathname, allMenuItems, t]);

  const isFullScreenPath = isFullScreenRoute(location.pathname);
  const displayName = isImpersonating
    ? impersonatedEmail
    : (profileDisplayName || userInfo?.user?.name || currentUser?.email || t('header_user'));

  return (
    <JobCenterProvider>
      <div className="flex h-screen flex-col bg-canvas font-sans text-ink antialiased">
        <ConnectionStatusBanner />
        <ImpersonationBanner />
        <div className="flex min-h-0 flex-1">
          {/* ===== Sidebar ===== */}
          <motion.aside
            initial={false}
            animate={{ width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH }}
            transition={{ type: 'spring', stiffness: 320, damping: 34 }}
            className="relative z-20 flex shrink-0 flex-col overflow-hidden border-r border-sidebar-line bg-sidebar print:hidden"
          >
            {/* Ambient top glow */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-48"
              style={{ background: 'radial-gradient(120% 70% at 30% 0%, rgba(59,130,246,0.16) 0%, rgba(99,102,241,0.06) 45%, transparent 75%)' }}
            />

            <div className={`relative flex h-16 shrink-0 items-center border-b border-sidebar-line ${collapsed ? 'justify-center px-0' : 'justify-between px-4'}`}>
              <Link to="/dashboard" className="flex items-center gap-3 transition-opacity hover:opacity-80">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-glow">
                  <img src={logo} alt="CLU" className="h-5 w-5 object-contain" />
                </span>
                {!collapsed && (
                  <span className="flex flex-col leading-none">
                    <span className="text-sm font-semibold tracking-tight text-sidebar-ink">CLU</span>
                    <span className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.22em] text-sidebar-muted">GRID Platform</span>
                  </span>
                )}
              </Link>
            </div>

            <nav className="relative flex-1 overflow-y-auto overflow-x-hidden px-3 py-4">
              {navSections.map(([section, items], sectionIndex) => (
                <div key={section} className={sectionIndex > 0 ? 'mt-6' : ''}>
                  {!collapsed && (
                    <div className="px-3 pb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-sidebar-muted">
                      {section}
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {items.map((item, index) => {
                      const itemPath = item.path.split('?')[0];
                      const isActive =
                        location.pathname === itemPath ||
                        (itemPath === '/settings/clu-admin' && location.pathname.startsWith('/settings/clu-admin'));
                      const Icon = resolveNavIcon(item.path, item.icon);
                      const label = item.nameKey ? t(item.nameKey) : item.name;
                      return (
                        <motion.div
                          key={item.path}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.03 * index, duration: 0.2 }}
                        >
                          <Link
                            to={item.path}
                            title={collapsed ? label : undefined}
                            className={`group relative flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                              collapsed ? 'justify-center' : 'gap-3'
                            } ${isActive ? 'text-white' : 'text-sidebar-muted hover:bg-sidebar-raised hover:text-sidebar-ink'}`}
                          >
                            {isActive && (
                              <motion.span
                                layoutId="nav-active-pill"
                                transition={{ type: 'spring', stiffness: 420, damping: 36 }}
                                className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-600/90 to-indigo-600/90 shadow-glow"
                              />
                            )}
                            <Icon className="relative z-10 h-[18px] w-[18px] shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
                            {!collapsed && (
                              <span className="relative z-10 flex w-full items-center justify-between">
                                <span className="truncate">{label}</span>
                                <span className="flex items-center gap-1.5">
                                  {item.path === '/control-center' && ccSummary && ccSummary.pending > 0 && (
                                    <span className="rounded-full bg-red-500/20 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-red-300 ring-1 ring-red-400/40">
                                      {ccSummary.pending}
                                    </span>
                                  )}
                                  {item.isDemo && (
                                    <span className="rounded-full bg-amber-400/15 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wide text-amber-300 ring-1 ring-amber-400/30">
                                      {t('layout_demo')}
                                    </span>
                                  )}
                                </span>
                              </span>
                            )}
                          </Link>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* Sidebar footer — search trigger */}
            <div className="relative border-t border-sidebar-line p-3">
              <button
                onClick={() => setPaletteOpen(true)}
                title={collapsed ? 'Search (⌘K)' : undefined}
                className={`flex w-full items-center rounded-xl border border-sidebar-line bg-sidebar-raised px-3 py-2.5 text-sm text-sidebar-muted transition-colors hover:border-line-strong hover:text-sidebar-ink ${
                  collapsed ? 'justify-center' : 'gap-3'
                }`}
              >
                <Search className="h-4 w-4 shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">Search...</span>
                    <kbd className="rounded-md border border-sidebar-line px-1.5 py-0.5 font-mono text-[10px]">⌘K</kbd>
                  </>
                )}
              </button>
            </div>
          </motion.aside>

          {/* ===== Main column ===== */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            {/* Header */}
            <header className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-line bg-surface px-4 print:hidden">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  onClick={() => setCollapsed(!collapsed)}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
                  aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {collapsed ? <PanelLeftOpen className="h-[18px] w-[18px]" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
                </button>
                {showMobileReturn && (
                  <button
                    onClick={() => navigate('/m/clu-ng')}
                    className="flex items-center gap-1.5 rounded-lg border border-line bg-surface-2 px-2.5 py-1.5 text-xs font-medium text-accent transition-colors hover:border-line-strong"
                    title="Back to mobile view"
                  >
                    <Smartphone className="h-3.5 w-3.5" />
                    Mobile
                  </button>
                )}
                <h1 className="truncate text-[15px] font-semibold tracking-tight text-ink">{pageTitle}</h1>
              </div>

              <div className="flex shrink-0 items-center gap-1.5">
                <OrganizationSelector />
                <LanguageSelector />
                {hookEffectiveRole && hookEffectiveRole !== 'user' && (
                  <div className="flex items-center gap-1">
                    <NotificationBell />
                    <JobCenterBell />
                  </div>
                )}

                <button
                  onClick={toggleTheme}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={theme}
                      initial={{ opacity: 0, rotate: -40, scale: 0.7 }}
                      animate={{ opacity: 1, rotate: 0, scale: 1 }}
                      exit={{ opacity: 0, rotate: 40, scale: 0.7 }}
                      transition={{ duration: 0.15 }}
                      className="flex"
                    >
                      {isDark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
                    </motion.span>
                  </AnimatePresence>
                </button>

                {/* User menu */}
                <Menu as="div" className="relative">
                  <Menu.Button className="flex items-center gap-2 rounded-xl border border-transparent px-1.5 py-1 transition-colors hover:border-line hover:bg-surface-2">
                    {profileAvatarUrl ? (
                      <img src={profileAvatarUrl} alt="User avatar" className="h-8 w-8 rounded-full border border-line object-cover" />
                    ) : (
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-xs font-semibold text-white">
                        {(displayName || 'U').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="hidden flex-col items-start leading-tight md:flex">
                      <span className="max-w-[140px] truncate text-xs font-semibold text-ink">{displayName}</span>
                      <span className={`font-mono text-[9px] font-medium uppercase tracking-[0.14em] ${isImpersonating ? 'text-amber-500 dark:text-amber-400' : 'text-ink-subtle'}`}>
                        {isImpersonating ? 'Impersonated' : getRoleDisplayName(getUserRole())}
                      </span>
                    </span>
                    <ChevronDown className="hidden h-3.5 w-3.5 text-ink-subtle md:block" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95 -translate-y-1"
                    enterTo="transform opacity-100 scale-100 translate-y-0"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-30 mt-2 w-64 origin-top-right overflow-hidden rounded-2xl border border-line bg-surface shadow-pop focus:outline-none">
                      <div className="border-b border-line bg-surface-2 px-4 py-3">
                        <p className="truncate text-sm font-semibold text-ink">
                          {isImpersonating ? impersonatedEmail : (userInfo?.user?.email || currentUser?.email || t('header_no_email'))}
                        </p>
                        <p className="mt-0.5 text-xs text-ink-muted">
                          {t('header_role')}:{' '}
                          <span className={`font-medium ${isImpersonating ? 'text-amber-500 dark:text-amber-400' : 'text-accent'}`}>
                            {isImpersonating ? 'Impersonated Session' : getRoleDisplayName(getUserRole())}
                          </span>
                        </p>
                        <p className="mt-0.5 truncate text-xs text-ink-subtle">
                          {t('header_organization')}: {getUserOrganization()}
                        </p>
                      </div>
                      <div className="p-1.5">
                        <Menu.Item>
                          {({ active }) => (
                            <Link to="/profile" className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-ink ${active ? 'bg-surface-2' : ''}`}>
                              <User className="h-4 w-4 text-ink-subtle" /> {t('header_my_profile')}
                            </Link>
                          )}
                        </Menu.Item>
                        {(hookEffectiveRole === 'admin' || isSuperUser) && (
                          <Menu.Item>
                            {({ active }) => (
                              <Link to="/billing" className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-ink ${active ? 'bg-surface-2' : ''}`}>
                                <Receipt className="h-4 w-4 text-ink-subtle" /> {t('header_my_billing')}
                              </Link>
                            )}
                          </Menu.Item>
                        )}
                        <Menu.Item>
                          {({ active }) => (
                            <Link to="/my-wallet" className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-ink ${active ? 'bg-surface-2' : ''}`}>
                              <Wallet className="h-4 w-4 text-ink-subtle" /> {t('header_my_wallet')}
                            </Link>
                          )}
                        </Menu.Item>
                        {isSuperUser && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => setShowBypassModal(true)}
                                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-ink ${active ? 'bg-surface-2' : ''}`}
                              >
                                <Zap className="h-4 w-4 text-ink-subtle" /> {t('header_admin_skip_onboarding')}
                              </button>
                            )}
                          </Menu.Item>
                        )}
                      </div>
                      <div className="border-t border-line p-1.5">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleBackToClassic}
                              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-ink-muted ${active ? 'bg-surface-2' : ''}`}
                            >
                              <Undo2 className="h-4 w-4 text-ink-subtle" /> Back to classic design
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleLogout}
                              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-red-500 dark:text-red-400 ${active ? 'bg-red-500/10' : ''}`}
                            >
                              <LogOut className="h-4 w-4" /> {t('header_logout')}
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </header>

            <PlatformNoticeBar />

            {/* Content */}
            <main className={`flex-1 ${isFullScreenPath ? 'overflow-hidden bg-surface p-0' : 'overflow-y-auto overflow-x-auto bg-canvas p-4 sm:p-6'}`}>
              <motion.div
                key={location.pathname}
                className={isFullScreenPath ? 'h-full' : undefined}
                initial={isFullScreenPath ? false : { opacity: 0, y: 6 }}
                animate={isFullScreenPath ? false : { opacity: 1, y: 0 }}
                transition={isFullScreenPath ? undefined : { duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <Outlet />
              </motion.div>
            </main>
          </div>
        </div>

        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={paletteItems} />

        <BypassOnboardingModal
          isOpen={showBypassModal}
          onClose={() => setShowBypassModal(false)}
          onSuccess={() => {}}
        />

        <BugReportButton />
      </div>
    </JobCenterProvider>
  );
};

export default LayoutV2;
